import { createMainPeerConnection, isDataChannelInit, isTransceiverInit, } from '@pexip/peer-connection';
import { createStatsCollector } from '@pexip/peer-connection-stats';
import { logger } from './logger';
import { getDirection, isAudioConfig, isMainConfig, isPresoConfig, isPresoVideo, } from './utils';
import { DEFAULT_CLIENT_CALLTYPE } from './constants';
import { createCall } from './call';
const createMCUPeerConnection = ({ pcMainSignals, peerOptions, callType, mediaStream, isDirectMedia, allowChangeOfMediaDirection, encodingPriorities, networkPriorities, degradationPreference, jitterBufferTarget, dataChannelId, eventSignals, polite, rtcConfig = peerOptions.rtcConfig, }) => {
    // If an m-line is present (even with direction = inactive), the MCU still treats it as a media call.
    // This breaks the intent of callType when you explicitly want to omit a media type from the transcoded call.
    // Therefore, when callType maps to 'inactive', omit the corresponding m-line entirely.
    const audioDirection = getDirection('audio', callType);
    const videoDirection = getDirection('video', callType);
    const presentationDirection = getDirection('presentation', callType);
    const [audioTrack] = mediaStream?.getAudioTracks() ?? [];
    const [videoTrack] = mediaStream?.getVideoTracks() ?? [];
    const streams = mediaStream && [mediaStream];
    // NOTE: to workaround the Chromium bug ref. https://github.com/pexip/aquila/issues/2779
    // No change to sendEncodings for Direct Media call to avoid the implicit rollback with
    // changes to the sender's params
    return createMainPeerConnection(pcMainSignals, {
        ...peerOptions,
        polite,
        rtcConfig,
        mediaInits: [
            isDirectMedia || audioDirection !== 'inactive'
                ? {
                    content: 'main',
                    direction: audioDirection,
                    kindOrTrack: audioTrack ?? 'audio',
                    // Registered even without a track yet, so the config
                    // picks up the stream's `addtrack` event.
                    ...(streams && {
                        streams,
                    }),
                    allowChangeOfMediaDirection: allowChangeOfMediaDirection.audio,
                    relativeDirection: isDirectMedia && audioDirection !== 'inactive',
                    sendEncodings: isDirectMedia
                        ? []
                        : [
                            {
                                ...(encodingPriorities.audio && {
                                    priority: encodingPriorities.audio,
                                }),
                                ...(networkPriorities?.audio && {
                                    networkPriority: networkPriorities?.audio,
                                }),
                            },
                        ],
                    ...(jitterBufferTarget?.audio !== undefined && {
                        jitterBufferTarget: jitterBufferTarget?.audio,
                    }),
                }
                : false,
            isDirectMedia || videoDirection !== 'inactive'
                ? {
                    content: 'main',
                    direction: videoDirection,
                    kindOrTrack: videoTrack ?? 'video',
                    ...(streams && {
                        streams,
                    }),
                    allowChangeOfMediaDirection: allowChangeOfMediaDirection.video,
                    relativeDirection: isDirectMedia && videoDirection !== 'inactive',
                    sendEncodings: isDirectMedia
                        ? []
                        : [
                            {
                                ...(encodingPriorities.video && {
                                    priority: encodingPriorities.video,
                                }),
                                ...(networkPriorities?.video && {
                                    networkPriority: networkPriorities?.video,
                                }),
                            },
                        ],
                    ...(degradationPreference?.video && {
                        degradationPreference: degradationPreference?.video,
                    }),
                    ...(jitterBufferTarget?.video !== undefined && {
                        jitterBufferTarget: jitterBufferTarget?.video,
                    }),
                }
                : false,
            isDirectMedia || presentationDirection !== 'inactive'
                ? {
                    content: 'slides',
                    direction: 'inactive',
                    kindOrTrack: 'video',
                    allowChangeOfMediaDirection: allowChangeOfMediaDirection.presentation,
                    sendEncodings: isDirectMedia
                        ? []
                        : [
                            {
                                ...(encodingPriorities.presentation && {
                                    priority: encodingPriorities.presentation,
                                }),
                                ...(networkPriorities?.presentation && {
                                    networkPriority: networkPriorities?.presentation,
                                }),
                            },
                        ],
                    ...(degradationPreference?.presentation && {
                        degradationPreference: degradationPreference?.presentation,
                    }),
                    ...(jitterBufferTarget?.presentation !== undefined && {
                        jitterBufferTarget: jitterBufferTarget?.presentation,
                    }),
                }
                : false,
            dataChannelId
                ? {
                    label: 'pexChannel',
                    negotiated: true,
                    id: dataChannelId,
                    eventListeners: [
                        {
                            event: 'message',
                            listener: event => {
                                try {
                                    const msg = JSON.parse(event.data);
                                    switch (msg.type) {
                                        case 'message':
                                            eventSignals.onMessage.emit({
                                                ...msg.body,
                                                direct: false,
                                            });
                                            break;
                                        case 'fecc':
                                            eventSignals.onFecc.emit(msg.body);
                                            break;
                                    }
                                }
                                catch (error) {
                                    logger.error({ error, event }, 'Failed to parse DataChannel event data from server.');
                                }
                            },
                        },
                    ],
                }
                : false,
        ].flatMap(init => isTransceiverInit(init) || isDataChannelInit(init) ? [init] : []),
    });
};
// -----------------------------------------------------------------------------
// MCU Strategy
// -----------------------------------------------------------------------------
/**
 * Creates an MCU-specific strategy for peer connection call lifecycle.
 *
 * Handles composite stream assembly (main tracks merged into one MediaStream),
 * presentation stream detection, and stats collector management for the
 * standard MCU (WEBRTC) call type.
 *
 * @example
 * ```ts
 * const strategy = createMCUStrategy(callOptions);
 * const call = createCall(callOptions, strategy);
 * ```
 */
const createMCUStrategy = (options) => {
    const { pcMainSignals, peerOptions, callType = DEFAULT_CLIENT_CALLTYPE, mediaStream, isDirectMedia, allowChangeOfMediaDirection = {
        video: true,
        audio: true,
        presentation: true,
    }, encodingPriorities = { video: 'low', audio: 'low', presentation: 'low' }, networkPriorities, degradationPreference, jitterBufferTarget, dataChannelId, eventSignals, mainStatsSignals, callSignals, } = options;
    const pcOptions = {
        pcMainSignals,
        peerOptions,
        callType,
        mediaStream,
        isDirectMedia,
        allowChangeOfMediaDirection,
        encodingPriorities,
        networkPriorities,
        degradationPreference,
        jitterBufferTarget,
        dataChannelId,
        eventSignals,
    };
    const statsCollectors = { inbound: {}, outbound: {} };
    return {
        createPC: (rtcConfig, polite) => createMCUPeerConnection({ ...pcOptions, rtcConfig, polite }),
        handleRemoteStreams: (transceiverConfig, peer) => {
            logger.debug({ transceiverConfig, isDirectMedia }, 'Received remote streams');
            const signal = isPresoConfig(transceiverConfig)
                ? callSignals.onRemotePresentationStream
                : callSignals.onRemoteStream;
            if (isDirectMedia && transceiverConfig.remoteStreams) {
                for (const stream of transceiverConfig.remoteStreams) {
                    logger.debug({ stream, tracks: stream.getTracks() }, 'Emits remote stream');
                    signal.emit(stream);
                }
                return;
            }
            logger.debug({
                transceiverConfig,
                transceiverConfigs: peer.getTransceiverConfigs(),
                remoteStreams: transceiverConfig.remoteStreams,
            }, 'Emits remote streams based on transceivers');
            if (transceiverConfig.remoteStreams?.[0]) {
                signal.emit(transceiverConfig.remoteStreams?.[0]);
            }
        },
        setupStats: peer => {
            for (const config of peer.getTransceiverConfigs()) {
                if (isMainConfig(config) && config.transceiver) {
                    statsCollectors.inbound[config.kind] = createStatsCollector({
                        input: config.transceiver.receiver,
                        signals: isAudioConfig(config)
                            ? mainStatsSignals.audioIn
                            : mainStatsSignals.videoIn,
                    });
                    statsCollectors.outbound[config.kind] =
                        createStatsCollector({
                            input: config.transceiver.sender,
                            signals: isAudioConfig(config)
                                ? mainStatsSignals.audioOut
                                : mainStatsSignals.videoOut,
                        });
                }
                if (isPresoVideo(config) && config.transceiver) {
                    statsCollectors.inbound.preso = createStatsCollector({
                        input: config.transceiver.receiver,
                        signals: mainStatsSignals.presoVideoIn,
                    });
                    statsCollectors.outbound.preso = createStatsCollector({
                        input: config.transceiver.sender,
                        signals: mainStatsSignals.presoVideoOut,
                    });
                }
            }
        },
        cleanupStats: () => {
            statsCollectors.inbound.audio?.cleanup();
            statsCollectors.outbound.audio?.cleanup();
            statsCollectors.inbound.video?.cleanup();
            statsCollectors.outbound.video?.cleanup();
            statsCollectors.inbound.preso?.cleanup();
            statsCollectors.outbound.preso?.cleanup();
        },
        resetStats: () => [
            statsCollectors.inbound.video?.resetStats(),
            statsCollectors.outbound.video?.resetStats(),
            statsCollectors.inbound.audio?.resetStats(),
            statsCollectors.outbound.audio?.resetStats(),
        ],
        wireStatsSignals: (_peer, handlePacketStagnation) => [
            mainStatsSignals.combinedRtcStatsSignal.add(([audioIn, audioOut, videoIn, videoOut, presoVideoIn, presoVideoOut,]) => {
                callSignals.onRtcStats.emit({
                    inbound: {
                        audio: audioIn,
                        video: videoIn,
                        preso: presoVideoIn,
                    },
                    outbound: {
                        audio: audioOut,
                        video: videoOut,
                        preso: presoVideoOut,
                    },
                });
            }),
            mainStatsSignals.combinedCallQualityStatsSignal.add(([audioIn, audioOut]) => {
                callSignals.onCallQualityStats.emit({
                    inbound: {
                        audio: (audioIn ?? []),
                    }, // FIXME: stats can be either AudioQualityStats | VideoQualityStats
                    outbound: {
                        audio: (audioOut ?? []),
                    },
                });
            }),
            mainStatsSignals.combinedCallQualitySignal.add(callSignals.onCallQuality.emit),
            mainStatsSignals.videoIn.onRtcStats.add(handlePacketStagnation),
        ],
    };
};
// -----------------------------------------------------------------------------
// createMCUCall — public entry point for MCU (WEBRTC) calls
// -----------------------------------------------------------------------------
/**
 * Creates an MCU (WEBRTC) call — the default call type.
 *
 * @example
 * ```ts
 * const call = createMCUCall(callOptions);
 * ```
 */
export const createMCUCall = (options) => createCall(options, createMCUStrategy(options));
//# sourceMappingURL=callMCU.js.map